import React, { useState } from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { Route } from '../../../types';
import type { RouteViewModel } from '../../../types/routeViewModel';
import type { CorrectionContext } from '../hooks/useCorrectionContext';
import { ResourceCorrectionPanel } from '../panels/ResourceCorrectionPanel';
import { StopCorrectionPanel } from '../panels/StopCorrectionPanel';
import { CargoSegmentWizardTrigger } from '../panels/CargoSegmentPanel';
import { AddUnloadWizard } from '../wizards/AddUnloadWizard';
import { CarrierReplacementWizard } from '../wizards/CarrierReplacementWizard';
import {
  CORRECTION_SCENARIOS,
  EARLY_COMPLETION_CARRIER_SUBFLOW_TITLE,
  type CorrectionScenarioId
} from '../scenarios/correctionScenarioTypes';
import {
  CARRIER_MULTI_APPLICATION_ROUTE_SCOPE_MESSAGE,
  isMultiApplicationRoute
} from '../wizards/carrierReplacementUxCopy';

const { Paragraph, Text } = Typography;

type Props = {
  scenarioId: CorrectionScenarioId;
  route: Route;
  routeId: number;
  view: RouteViewModel;
  ctx: CorrectionContext;
  onSuccess: () => void;
};

export function CorrectionOperationEditor({
  scenarioId,
  route,
  routeId,
  view,
  ctx,
  onSuccess
}: Props) {
  const scenario = CORRECTION_SCENARIOS.find((s) => s.id === scenarioId);
  const [unloadWizardOpen, setUnloadWizardOpen] = useState(false);
  const [carrierWizardOpen, setCarrierWizardOpen] = useState(false);

  return (
    <div className="mutation-workspace__section">
      {scenario?.description ? (
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          {scenario.description}
        </Paragraph>
      ) : null}

      {scenarioId === 'RESOURCE_CHANGE' && (
        <ResourceCorrectionPanel routeId={routeId} view={view} ctx={ctx} onSuccess={onSuccess} />
      )}

      {scenarioId === 'STOP_CHANGE' && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <StopCorrectionPanel
            routeId={routeId}
            view={view}
            ctx={ctx}
            onSuccess={onSuccess}
            variant="compact"
          />

          <div>
            <div className="mutation-workspace__section-title">Дополнительная выгрузка</div>
            {ctx.isOperationAllowed('ADD_UNLOAD') ? (
              <>
                <Button icon={<PlusOutlined />} onClick={() => setUnloadWizardOpen(true)}>
                  Добавить выгрузку
                </Button>
                <AddUnloadWizard
                  routeId={routeId}
                  view={view}
                  ctx={ctx}
                  open={unloadWizardOpen}
                  onClose={() => setUnloadWizardOpen(false)}
                  onSuccess={() => {
                    setUnloadWizardOpen(false);
                    onSuccess();
                  }}
                />
              </>
            ) : (
              <Text type="secondary">Добавление выгрузки недоступно.</Text>
            )}
          </div>

          <CargoSegmentWizardTrigger
            routeId={routeId}
            view={view}
            ctx={ctx}
            onSuccess={onSuccess}
          />
        </Space>
      )}

      {scenarioId === 'ADD_CARGO' && (
        <CargoSegmentWizardTrigger
          routeId={routeId}
          view={view}
          ctx={ctx}
          onSuccess={onSuccess}
          buttonLabel="Начать догруз"
          autoOpen
        />
      )}

      {scenarioId === 'EARLY_COMPLETION' && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div className="mutation-workspace__section-title">{EARLY_COMPLETION_CARRIER_SUBFLOW_TITLE}</div>
            {ctx.isOperationAllowed('CARRIER_REPLACEMENT') ? (
              <>
                {isMultiApplicationRoute(view) ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="Маршрут с несколькими заявками"
                    description={CARRIER_MULTI_APPLICATION_ROUTE_SCOPE_MESSAGE}
                  />
                ) : null}
                <Paragraph type="secondary">
                  Завершение текущего маршрута, складской handoff и ручное создание нового маршрута через
                  стандартный Route Create (без автосоздания). Завершение всегда ко всему маршруту, не к одной
                  заявке.
                </Paragraph>
                <Button type="primary" onClick={() => setCarrierWizardOpen(true)}>
                  {EARLY_COMPLETION_CARRIER_SUBFLOW_TITLE}
                </Button>
              </>
            ) : (
              <Alert
                type="warning"
                showIcon
                message={`${EARLY_COMPLETION_CARRIER_SUBFLOW_TITLE} недоступна`}
                description="Доступно после полного завершения исполнения и выгрузки остатка на склад."
              />
            )}
          </div>

          <CarrierReplacementWizard
            route={route}
            routeId={routeId}
            view={view}
            ctx={ctx}
            open={carrierWizardOpen}
            onClose={() => setCarrierWizardOpen(false)}
            onSuccess={onSuccess}
          />
        </Space>
      )}
    </div>
  );
}
